import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import {
  pauseRestoredPendingMutations,
  shouldDehydrateMutation as shouldDehydrateOfflineMutation,
} from '../mutations/queueBounds';
import { QUERY_CACHE_BUSTER } from './cacheVersion';
import { boundMessageQueriesInDehydratedClient } from './messagePersist';
import {
  estimatePersistBytes,
  MAX_PERSIST_BYTES,
  prunePersistedPayload,
} from './prunePersistedClient';
import { PERSIST_DEBOUNCE_MS, PERSIST_MAX_WAIT_MS, PERSIST_THROTTLE_MS } from './persistTiming';
import { appLog } from '../appLog';
const PERSIST_KEY_PREFIX = 'prii-query-cache';
const PERSIST_RESTORE_TIMEOUT_MS = 5000;

function withRestoreTimeout(restoreFn, timeoutMs = PERSIST_RESTORE_TIMEOUT_MS) {
  return async () => {
    let timer;
    try {
      return await Promise.race([
        restoreFn(),
        new Promise((resolve) => {
          timer = setTimeout(() => {
            console.warn('[query] persist restore timed out; continuing without restored cache');
            resolve(undefined);
          }, timeoutMs);
        }),
      ]);
    } catch (err) {
      console.warn('[query] persist restore failed:', err);
      return undefined;
    } finally {
      if (timer) clearTimeout(timer);
    }
  };
}

function wrapPersisterWithRestoreTimeout(persister) {
  if (!persister?.restoreClient) return persister;
  const restoreClient = withRestoreTimeout(async () =>
    pauseRestoredPendingMutations(await persister.restoreClient()),
  );
  return { ...persister, restoreClient };
}

/** User-scoped key — prevents cross-account restore on cold start. */
export function getPersistStorageKey(userId) {
  const scope = userId || 'anon';
  return `${PERSIST_KEY_PREFIX}-${QUERY_CACHE_BUSTER}-${scope}`;
}

function serializePersistedClient(persistedClient) {
  const bounded = boundMessageQueriesInDehydratedClient(persistedClient);
  return prunePersistedPayload(JSON.stringify(bounded), MAX_PERSIST_BYTES);
}

function createSafeLocalStorage() {
  if (typeof window === 'undefined') return undefined;

  return {
    getItem(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (err) {
        console.warn('[query] localStorage.getItem failed:', err);
        return null;
      }
    },
    setItem(key, value) {
      try {
        const bytes = estimatePersistBytes(value);
        if (bytes > MAX_PERSIST_BYTES) {
          console.warn(
            `[query] persist payload still ~${Math.round(bytes / 1024)}KB after pruning; skipping`,
          );
          return;
        }
        window.localStorage.setItem(key, value);
      } catch (err) {
        const isQuota =
          err?.name === 'QuotaExceededError' || err?.code === 22 || err?.code === 1014;
        console.warn('[query] localStorage.setItem failed:', err);
        if (isQuota) {
          try {
            const pruned = prunePersistedPayload(value, MAX_PERSIST_BYTES * 0.75);
            window.localStorage.removeItem(key);
            window.localStorage.setItem(key, pruned);
          } catch (retryErr) {
            console.warn('[query] localStorage quota retry failed:', retryErr);
          }
        }
      }
    },
    removeItem(key) {
      try {
        window.localStorage.removeItem(key);
      } catch (err) {
        console.warn('[query] localStorage.removeItem failed:', err);
      }
    },
  };
}

function createCapacitorPreferencesStorage() {
  return {
    getItem: async (key) => {
      try {
        const { value } = await Preferences.get({ key });
        return value ?? null;
      } catch (err) {
        console.warn('[query] Preferences.get failed:', err);
        return null;
      }
    },
    setItem: async (key, value) => {
      try {
        await Preferences.set({ key, value });
      } catch (err) {
        console.warn('[query] Preferences.set failed:', err);
        try {
          const pruned = prunePersistedPayload(value, MAX_PERSIST_BYTES * 0.75);
          await Preferences.set({ key, value: pruned });
        } catch (retryErr) {
          console.warn('[query] Preferences.set retry failed:', retryErr);
        }
      }
    },
    removeItem: async (key) => {
      try {
        await Preferences.remove({ key });
      } catch (err) {
        console.warn('[query] Preferences.remove failed:', err);
      }
    },
  };
}

/**
 * Debounced async persister — coalesces many cache mutations into few native writes.
 * TanStack's asyncThrottle fires once per interval; staggered query updates during
 * startup still produce dozens of bridge round-trips. Debounce + maxWait batches
 * the full dehydrated client and only writes after the cache settles.
 */
function writeSyncMirror(key, serialized) {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(key, serialized);
    return true;
  } catch (err) {
    console.warn('[query] localStorage sync mirror write failed:', err);
    // Never leave an older copy behind: the restore reads the mirror first, so a stale one would win
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* nothing to remove */
    }
    return false;
  }
}

function readSyncMirror(key) {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch (err) {
    console.warn('[query] localStorage sync mirror read failed:', err);
    return null;
  }
}

function createDebouncedAsyncPersister({ storage, key, debounceMs, maxWaitMs }) {
  let pendingClient = null;
  let debounceTimer = null;
  let maxWaitTimer = null;
  let maxWaitStartedAt = 0;
  let writeChain = Promise.resolve();
  let writeCount = 0;

  const logWrite = (reason) => {
    writeCount += 1;
    if (import.meta.env.DEV) {
      const bytes = pendingClient ? estimatePersistBytes(JSON.stringify(pendingClient)) : 0;
      console.log(
        `[query] persist write #${writeCount} (${reason}, ~${Math.round(bytes / 1024)}KB)`,
      );
    }
  };

  const executeWrite = async (reason) => {
    if (debounceTimer != null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    if (maxWaitTimer != null) {
      clearTimeout(maxWaitTimer);
      maxWaitTimer = null;
    }
    maxWaitStartedAt = 0;

    if (!pendingClient) return;
    const client = pendingClient;
    pendingClient = null;

    logWrite(reason);
    const t0 = performance.now();
    const serialized = serializePersistedClient(client);
    writeSyncMirror(key, serialized);
    const mainMs = Math.round(performance.now() - t0);
    await storage.setItem(key, serialized);
    // The save runs on the main thread: a slow one is a moment the app can't answer a tap
    if (mainMs > 60)
      appLog('warn', 'perf.cache_write', {
        mainMs,
        kb: Math.round(serialized.length / 1024),
        reason,
      });
  };

  const writeSync = (persistedClient, reason = 'sync') => {
    pendingClient = persistedClient;
    const serialized = serializePersistedClient(persistedClient);
    writeSyncMirror(key, serialized);
    if (import.meta.env.DEV) {
      console.log(
        `[query] persist sync mirror (${reason}, ~${Math.round(estimatePersistBytes(serialized) / 1024)}KB)`,
      );
    }
    void storage.setItem(key, serialized).catch((err) => {
      console.warn('[query] persist async write after sync mirror failed:', err);
    });
  };

  const queueWrite = (reason) => {
    writeChain = writeChain
      .then(() => executeWrite(reason))
      .catch((err) => console.warn('[query] persist write failed:', err));
    return writeChain;
  };

  const scheduleDebouncedWrite = () => {
    if (debounceTimer != null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      queueWrite('debounced');
    }, debounceMs);

    if (maxWaitStartedAt === 0) {
      maxWaitStartedAt = Date.now();
      maxWaitTimer = setTimeout(() => {
        queueWrite('max-wait');
      }, maxWaitMs);
    } else if (Date.now() - maxWaitStartedAt >= maxWaitMs) {
      queueWrite('max-wait-elapsed');
    }
  };

  const flush = () => queueWrite('flush');

  return {
    persistClient: async (persistedClient) => {
      pendingClient = persistedClient;
      scheduleDebouncedWrite();
    },
    restoreClient: async () => {
      // The web view's own copy first: it is written with every save (or removed when it can't be) and reads at once.
      // Preferences goes over the native bridge - a few MB took long enough on a phone that the 5 s restore timeout
      // could start the app with nothing, and every page then waited on the network (2026-10-30 audit).
      const t0 = performance.now();
      let source = 'mirror';
      let cacheString = readSyncMirror(key);
      if (!cacheString) {
        source = 'native';
        cacheString = await storage.getItem(key);
      }
      const readMs = Math.round(performance.now() - t0);
      if (!cacheString) return undefined;
      try {
        const t1 = performance.now();
        const parsed = JSON.parse(cacheString);
        appLog('info', 'perf.cache_restore', {
          source,
          readMs,
          parseMs: Math.round(performance.now() - t1),
          kb: Math.round(cacheString.length / 1024),
        });
        return parsed;
      } catch (err) {
        console.warn('[query] failed to parse persisted cache:', err);
        return undefined;
      }
    },
    removeClient: () => {
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(key);
        } catch (err) {
          console.warn('[query] localStorage mirror remove failed:', err);
        }
      }
      return storage.removeItem(key);
    },
    flush,
    writeSync,
    storageKey: key,
  };
}

/** Keys we persist across cold starts — message threads bounded at serialize time. */
/**
 * Only what you are going to is saved for later (Sam, 2026-10-26): public festivals show on Sets and Maps without an
 * RSVP, and a lineup you only browsed lives while the app is open, never in the saved cache - it would compete for the
 * 4 MB with the festivals you are going to. The lists themselves are saved, like the Events feed. `goingIds` is the set
 * of event ids you are going to (from the rsvps cache); without it everything is kept, as before.
 */
export function keepLineupFor(query, goingIds) {
  const k = query.queryKey;
  if (!goingIds || k[0] !== 'events' || k[1] !== 'lineup') return true;
  return goingIds.has(String(k[2]));
}

export function shouldDehydrateQuery(query, goingIds = null) {
  const queryKey = query.queryKey;
  if (!Array.isArray(queryKey) || queryKey.length === 0) return false;

  const { status, fetchStatus } = query.state ?? {};
  // Never persist in-flight or unresolved queries — pending promises reject on rehydrate.
  if (status !== 'success') return false;
  // A map payload lives in the map store (lib/map/mapStore.js), not in this ~4 MB blob. The lists stay.
  if (queryKey[0] === 'festival-map' && queryKey[1] !== 'mine' && queryKey[1] !== 'upcoming')
    return false;
  // Studio is an online desktop tool: nothing of it rides the app's persisted cache.
  if (queryKey[0] === 'studio') return false;
  if (fetchStatus === 'fetching' || fetchStatus === 'paused') return false;
  if (!keepLineupFor(query, goingIds)) return false;

  return true;
}

export function shouldDehydrateMutation(mutation) {
  return shouldDehydrateOfflineMutation(mutation);
}

export function createQueryPersister(userId) {
  const key = getPersistStorageKey(userId);
  const useNativeStorage = Capacitor.isNativePlatform();

  if (useNativeStorage) {
    return wrapPersisterWithRestoreTimeout(
      createDebouncedAsyncPersister({
        storage: createCapacitorPreferencesStorage(),
        key,
        debounceMs: PERSIST_DEBOUNCE_MS.native,
        maxWaitMs: PERSIST_MAX_WAIT_MS.native,
      }),
    );
  }

  const webPersister = wrapPersisterWithRestoreTimeout(
    createSyncStoragePersister({
      storage: createSafeLocalStorage(),
      key,
      throttleTime: PERSIST_THROTTLE_MS,
      serialize: (client) => serializePersistedClient(client),
    }),
  );

  return wrapWebPersisterWithSyncWrite(webPersister, key);
}

/** Extend web sync persister with writeSync for background flush parity. */
function wrapWebPersisterWithSyncWrite(persister, key) {
  if (!persister) return persister;
  return {
    ...persister,
    writeSync(persistedClient, reason = 'sync') {
      const serialized = serializePersistedClient(persistedClient);
      writeSyncMirror(key, serialized);
      if (import.meta.env.DEV) {
        console.log(`[query] persist sync write (${reason})`);
      }
      try {
        persister.persistClient(persistedClient);
      } catch (err) {
        console.warn('[query] web persistClient after sync write failed:', err);
      }
    },
    storageKey: key,
  };
}

export function removePersistedQueryCache(userId) {
  const key = getPersistStorageKey(userId);
  if (Capacitor.isNativePlatform()) {
    Preferences.remove({ key }).catch((err) => {
      console.warn('[query] Preferences.remove failed:', err);
    });
    return;
  }
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      console.warn('[query] localStorage.removeItem failed:', err);
    }
  }
}

/** @deprecated Use getPersistStorageKey(userId) — kept for legacy cleanup. */
export const PERSISTED_CACHE_STORAGE_KEY = `${PERSIST_KEY_PREFIX}-${QUERY_CACHE_BUSTER}`;
